import React, { useState, useEffect } from "react";
import { useAuth } from "../../../context/AuthContext";
import { updateUserProfile } from "../../../utils/userApi";
import StatusModal from "../../../components/modals/statusModal/StatusModal";
import InstitutionSelect from "../../../components/institutionSelect/InstitutionSelect";

// Import shared constants
import { DEGREE_OPTIONS, FIELD_OF_STUDY_GROUPS } from "../../../data/nigerianInstitutions";

// Flat list for search (derived from the groups)
const ALL_FIELDS = FIELD_OF_STUDY_GROUPS.flatMap((g) => g.fields);

// ── Searchable Field of Study Select ─────────────────────────────────────────
const FieldOfStudySelect = ({ value, onChange, error }) => {
  const [query, setQuery] = useState(value || "");
  const [isOpen, setIsOpen] = useState(false);
  
  const ref = React.useRef(null);

  // Sync query when external value changes
  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handler = (e) => {
      if (!ref.current?.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Filter groups based on search query
  const filteredGroups = query.trim().length < 1
    ? FIELD_OF_STUDY_GROUPS
    : FIELD_OF_STUDY_GROUPS.reduce((acc, g) => {
        const items = g.fields.filter((f) =>
          f.toLowerCase().includes(query.toLowerCase())
        );
        if (items.length) acc.push({ ...g, fields: items });
        return acc;
      }, []);

  const handleSelect = (field) => {
    setQuery(field);
    onChange(field);
    setIsOpen(false);
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          border: `1px solid ${error ? "#EF4444" : isOpen ? "#6366F1" : "#E0E0E0"}`,
          borderRadius: "8px",
          padding: "0 10px",
          height: "42px",
          background: "#fff",
          boxShadow: isOpen ? "0 0 0 3px rgba(99,102,241,0.1)" : "none",
          transition: "border-color 0.15s, box-shadow 0.15s",
        }}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, color: "#AAA", marginRight: "8px" }}>
          <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.5" />
          <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            onChange(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder="Search or select field of study..."
          style={{
            flex: 1,
            border: "none",
            outline: "none",
            fontSize: "14px",
            background: "transparent",
            fontFamily: "inherit",
            color: "#111",
          }}
          autoComplete="off"
        />
        {query && (
          <button
            type="button"
            onClick={() => {
              setQuery("");
              onChange("");
            }}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#AAA", fontSize: "11px", padding: "2px 4px" }}
          >
            ✕
          </button>
        )}
      </div>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            background: "#fff",
            border: "1px solid #E8E8E8",
            borderRadius: "10px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
            zIndex: 999,
            maxHeight: "260px",
            overflowY: "auto",
          }}
        >
          {filteredGroups.length === 0 ? (
            <div style={{ padding: "1rem", textAlign: "center" }}>
              <p style={{ fontSize: "13px", color: "#555", margin: "0 0 8px" }}>
                No match for "<strong>{query}</strong>"
              </p>
              <button
                type="button"
                onClick={() => {
                  onChange(query);
                  setIsOpen(false);
                }}
                style={{
                  background: "#F5F5FF",
                  border: "1px solid #A5B4FC",
                  borderRadius: "6px",
                  fontSize: "13px",
                  color: "#4F46E5",
                  padding: "6px 14px",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                Use "{query}"
              </button>
            </div>
          ) : (
            filteredGroups.map((g) => (
              <div key={g.group}>
                <div
                  style={{
                    fontSize: "10px",
                    fontWeight: 700,
                    color: "#AAA",
                    textTransform: "uppercase",
                    letterSpacing: "0.07em",
                    padding: "6px 14px 3px",
                  }}
                >
                  {g.group}
                </div>
                {g.fields.map((field) => (
                  <button
                    key={field}
                    type="button"
                    onClick={() => handleSelect(field)}
                    style={{
                      display: "block",
                      width: "100%",
                      background: value === field ? "#ECEEFF" : "none",
                      border: "none",
                      padding: "7px 14px",
                      textAlign: "left",
                      fontSize: "13px",
                      color: value === field ? "#4F46E5" : "#222",
                      fontWeight: value === field ? 500 : 400,
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                    onMouseEnter={(e) => {
                      if (value !== field) e.currentTarget.style.background = "#F5F5FF";
                    }}
                    onMouseLeave={(e) => {
                      if (value !== field) e.currentTarget.style.background = "none";
                    }}
                  >
                    {field}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      )}

      {error && <p style={{ fontSize: "12px", color: "#EF4444", margin: "4px 0 0" }}>{error}</p>}
    </div>
  );
};

// ── Main CareerForm ───────────────────────────────────────────────────────────
const CareerForm = ({ onCancel }) => {
  const { user, updateUser } = useAuth();

  const [isSaving, setIsSaving] = useState(false);
  const [statusModal, setStatusModal] = useState({
    open: false,
    type: "",
    message: "",
    subtext: "",
  });

  const [formData, setFormData] = useState({
    schoolName:        user?.schoolName       || "",
    degree:            user?.degree           || "",
    degreeOther:       user?.degreeOther      || "",
    fieldOfStudy:      user?.fieldOfStudy     || "",
    fieldOfStudyOther: user?.fieldOfStudyOther || "",
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const errs = {};
    if (!formData.schoolName?.trim()) errs.schoolName = "School name is required.";
    if (!formData.degree) errs.degree = "Please select a degree.";
    if (formData.degree === "other" && !formData.degreeOther?.trim()) {
      errs.degreeOther = "Please specify your degree.";
    }
    if (!formData.fieldOfStudy?.trim()) errs.fieldOfStudy = "Field of study is required.";

    return errs;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    try {
      setIsSaving(true);

      const payload = Object.fromEntries(
        Object.entries(formData).filter(([_, value]) => value !== "" && value !== null)
      );

      const response = await updateUserProfile(payload);
      updateUser(response.user);

      setStatusModal({
        open: true,
        type: "success",
        message: "Career Updated Successfully",
        subtext: "Your education details have been saved.",
      });

      setTimeout(() => onCancel(), 1200);
    } catch (err) {
      console.error("Career update failed:", err);
      setStatusModal({
        open: true,
        type: "error",
        message: "Update Failed",
        subtext: err.response?.data?.message || "Failed to update career details. Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form className="edit-form-card" onSubmit={handleSave}>
      <h4 className="form-title">Update Education Information</h4>

      {/* School Name */}
      <div className="form-group">
        <label>Name of School *</label>
        <InstitutionSelect
          value={formData.schoolName}
          onChange={(val) => {
            setFormData((prev) => ({ ...prev, schoolName: val }));
            if (errors.schoolName) setErrors((prev) => ({ ...prev, schoolName: "" }));
          }}
          error={errors.schoolName}
          placeholder="Search university, polytechnic, college..."
        />
      </div>

      {/* Degree */}
      <div className="form-group">
        <label>Degree *</label>
        <select
          name="degree"
          value={formData.degree}
          onChange={handleChange}
          className="tutor-input-field"
        >
          <option value="">Select degree</option>
          {DEGREE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {errors.degree && <p className="error">{errors.degree}</p>}
      </div>

      {formData.degree === "other" && (
        <div className="form-group">
          <label>Specify Degree *</label>
          <input
            name="degreeOther"
            value={formData.degreeOther}
            onChange={handleChange}
            placeholder="Enter your degree"
            className="tutor-input-field"
          />
          {errors.degreeOther && <p className="error">{errors.degreeOther}</p>}
        </div>
      )}

      {/* Field of Study */}
      <div className="form-group">
        <label>Field of Study *</label>
        <FieldOfStudySelect
          value={formData.fieldOfStudy}
          onChange={(val) => {
            setFormData((prev) => ({ ...prev, fieldOfStudy: val }));
            if (errors.fieldOfStudy) setErrors((prev) => ({ ...prev, fieldOfStudy: "" }));
          }}
          error={errors.fieldOfStudy}
        />
      </div>

      <div className="form-actions">
        <button
          type="button"
          className="edit-cancel-btn"
          onClick={onCancel}
          disabled={isSaving}
        >
          Cancel
        </button>

        <button
          type="submit"
          className="edit-primary-btn"
          disabled={isSaving}
        >
          {isSaving ? "Saving..." : "Update Career"}
        </button>
      </div>

      <StatusModal
        open={statusModal.open}
        type={statusModal.type}
        message={statusModal.message}
        subtext={statusModal.subtext}
        onClose={() => setStatusModal((prev) => ({ ...prev, open: false }))}
      />
    </form>
  );
};

export default CareerForm;